import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Radio } from '@creditornot/cb-radio';
import { ModalHeader, ModalFooter, ModalContent } from '@creditornot/cbt-modal';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { IntlShape, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { getInvoiceReport, getPaymentDocumentPdfLink } from 'apis/invoicing-service/api';
import { VenuePayout } from 'apis/merchant-payout-service/types';
import { fetchInvoiceDossier, getSupplementriesPdfLink } from 'apis/restaurant-api/merchant-admin/payouts/api';
import { InvoiceType, SupplementariesDocumentType } from 'apis/restaurant-api/merchant-admin/payouts/types';
import { getWolfFileByUrl } from 'apis/wolf/api';
import { APIErrorNotification, WoltModal } from 'common/components';
import { useFetch } from 'common/hooks';
import { downloadFileFromAxiosResponse, downloadFromUrl } from 'common/utils/download';

const StyledWoltModal = styled(WoltModal)`
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  width: 640px;
`;

const StyledContent = styled(ModalContent)`
  display: flex;
`;

type Props = {
  onClose: () => void;
  payoutReport: VenuePayout;
  intl: IntlShape;
  venueId: string;
};

const StyledRadio = styled(Radio)`
  & + & {
    margin-block-start: 8px;
  }
`;

const intervalTranslations = {
  custom: 'payout-reports.custom',
  hexa_monthly: 'payout-reports.hexa-monthly',
  monthly: 'payout-reports.monthly',
  semi_monthly: 'payout-reports.semi-monthly',
  semi_weekly: 'payout-reports.semi-weekly',
  weekly: 'payout-reports.weekly',
};

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const RadioWrapper = styled.div``;

const LeftContent = styled.p`
  margin-inline-end: 136px;
`;

const INVOICE_TYPE: { [key in InvoiceType | SupplementariesDocumentType]: string } = {
  merchant_invoice: 'payout-reports.merchant_invoice',
  wolt_invoice: 'payout-reports.wolt_invoice',
  ipad_invoice: 'payout-reports.ipad_invoice',
  photos_invoice: 'payout-reports.photos_invoice',
  printer_invoice: 'payout-reports.printer_invoice',
  sales_report: 'payout-reports.report',
  netting_report: 'payout-reports.netting_report',
  credit_note: 'payout-reports.credit_note',
  correction_invoice: 'payout-reports.correction_invoice',
};

enum DocumentCategory {
  INVOICE = 'invoice',
  PAYMENT_DOCUMENT = 'paymentdocument',
  SUPPLEMENTARY = 'supplementary',
}

export const PayoutReportListModal = ({ onClose, payoutReport, intl, venueId }: Props) => {
  const [selectedInvoiceType, setSelectedInvoiceType] = useState('wolt_invoice');
  const [invoiceIndex, setInvoiceIndex] = useState<number | undefined>(undefined);
  const [invoicingServiceIdOrPath, setInvoicingServiceIdOrPath] = useState<string | undefined>(undefined);
  const [selectedDocumentCategory, setSelectedDocumentCategory] = useState<DocumentCategory | undefined>(undefined);

  const { id: payoutId } = payoutReport;
  const { data, error } = useFetch(() => fetchInvoiceDossier(venueId, payoutId), [venueId, payoutId]);
  const t = (id: string) => intl.formatMessage({ id });

  const handleDownloadInvoice = async () => {
    try {
      switch (selectedDocumentCategory) {
        case DocumentCategory.INVOICE:
          const pdfUrl = await getInvoiceReport(invoicingServiceIdOrPath);
          downloadFileFromAxiosResponse(pdfUrl);
          break;
        case DocumentCategory.SUPPLEMENTARY:
          const supplementriesPdfUrl = await getSupplementriesPdfLink(venueId, invoicingServiceIdOrPath);
          downloadFromUrl(supplementriesPdfUrl, `${invoicingServiceIdOrPath}`);
          break;
        case DocumentCategory.PAYMENT_DOCUMENT:
          const paymentDocumentPdfUrl = await getPaymentDocumentPdfLink(invoicingServiceIdOrPath);
          const paymentDocumentPdfBlob = await getWolfFileByUrl(paymentDocumentPdfUrl.url);
          downloadFileFromAxiosResponse(paymentDocumentPdfBlob);
          break;
      }
    } catch (e) {
      toast.error('Invoice generation failed.');
    }
  };

  const handleChange = (
    documentCategory: DocumentCategory,
    invoiceType: InvoiceType | SupplementariesDocumentType,
    id: string,
    idx?: number,
  ) => {
    setSelectedInvoiceType(invoiceType);
    setInvoiceIndex(idx);
    setInvoicingServiceIdOrPath(id);
    setSelectedDocumentCategory(documentCategory);
  };

  useEffect(() => {
    setInvoicingServiceIdOrPath(data?.invoices[0].invoicing_service_id);
  }, [data]);

  return (
    <>
      {error && <APIErrorNotification error={error} />}
      {data && (
        <StyledWoltModal isOpen>
          <ModalHeader style={{ paddingLeft: 16 }} onCloseClick={onClose}>
            {t('payout-reports.report')} {t(intervalTranslations[payoutReport.interval])} {payoutReport.intervalNumber}
          </ModalHeader>

          <StyledContent>
            <LeftContent>Document</LeftContent>
            <RightContent>
              {data.invoices
                .filter(({ status }) => status === 'issued')
                .map(({ invoice_type, invoicing_service_id }, index) => (
                  <RadioWrapper key={invoice_type}>
                    <StyledRadio
                      checked={index === invoiceIndex}
                      value={index}
                      onChange={() => handleChange(DocumentCategory.INVOICE, invoice_type, invoicing_service_id, index)}
                    >
                      <FormattedMessage id={INVOICE_TYPE[invoice_type]} />
                    </StyledRadio>
                  </RadioWrapper>
                ))}
              {data.supplementaries.map(report => (
                <RadioWrapper key={report.document_type}>
                  <StyledRadio
                    checked={report.document_type === selectedInvoiceType}
                    value={report.document_type}
                    onChange={() => handleChange(DocumentCategory.SUPPLEMENTARY, report.document_type, report.url)}
                  >
                    <FormattedMessage id={INVOICE_TYPE[report.document_type]} />
                  </StyledRadio>
                </RadioWrapper>
              ))}
              {data.payment_documents
                .filter(({ status }) => status === 'issued')
                .map(document => (
                  <RadioWrapper key={document.type}>
                    <StyledRadio
                      checked={document.type === selectedInvoiceType}
                      value={document.type}
                      onChange={() =>
                        handleChange(DocumentCategory.PAYMENT_DOCUMENT, document.type, document.invoicing_service_id)
                      }
                    >
                      <FormattedMessage id={INVOICE_TYPE[document.type]} />
                    </StyledRadio>
                  </RadioWrapper>
                ))}
            </RightContent>
          </StyledContent>

          <ModalFooter style={{ justifyContent: 'flex-end' }}>
            <Button size="small" variant="primary" onClick={handleDownloadInvoice}>
              Download
            </Button>
          </ModalFooter>
        </StyledWoltModal>
      )}
    </>
  );
};
